import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { formatHubMedNoteDateTime } from "@/utils/hubMedFormatting";
import { DOCLINK_COLORS } from "@/config/colors";

const NOTE_LEFT_BORDERS = [
  DOCLINK_COLORS.MID_PURPLE,
  "#0d9488",
  "#d97706",
  "#2563eb",
  "#be185d",
  "#15803d",
];

const HubMedCommentsTimeline = ({ comments }) => {
  const sorted = useMemo(() => {
    const list = Array.isArray(comments) ? [...comments] : [];
    list.sort((a, b) => {
      const ta = new Date(a.AddedDateTime || a.addedDateTime || 0).getTime();
      const tb = new Date(b.AddedDateTime || b.addedDateTime || 0).getTime();
      return tb - ta;
    });
    return list;
  }, [comments]);

  if (!sorted.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 0.5 }}>
        No notes yet.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        maxHeight: 320,
        overflowY: "auto",
        overflowX: "hidden",
        pr: 0.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
    >
      {sorted.map((c, i) => (
        <Box
          key={c.CommentID ?? c.commentId ?? i}
          sx={{
            borderRadius: 2,
            border: "1px solid",
            borderColor: "rgba(64, 29, 94, 0.12)",
            borderLeftWidth: 4,
            borderLeftStyle: "solid",
            borderLeftColor: NOTE_LEFT_BORDERS[i % NOTE_LEFT_BORDERS.length],
            bgcolor: "background.paper",
            px: 2,
            py: 1.5,
            boxShadow: "0 1px 3px rgba(64, 29, 94, 0.06)",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              mb: 1.25,
              color: "text.primary",
              lineHeight: 1.5,
            }}
          >
            {c.Comment ?? c.comment}
          </Typography>
          <Box
            component="div"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 0.75,
              rowGap: 0.25,
            }}
          >
            <Typography variant="caption" fontWeight={600} color="text.primary">
              {c.UserDisplayName || c.userDisplayName || "User"}
            </Typography>
            <Typography variant="caption" color="text.disabled" aria-hidden>
              ·
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatHubMedNoteDateTime(c.AddedDateTime || c.addedDateTime)}
            </Typography>
          </Box>
          {(c.UserEmail || c.userEmail) ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.75, opacity: 0.75 }}
            >
              {c.UserEmail || c.userEmail}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Box>
  );
};

export default HubMedCommentsTimeline;
